import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/motion";
import { api, formatError } from "../lib/api";
import { CONTACT_EMAIL } from "../lib/content";

const SUBJECTS = ["Consulting", "Products", "Field trials & R&D", "Partnership", "General"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="label-caps !text-[10px] text-moss">{label}</span>
    {children}
  </label>
);

const inputCls = "mt-2 w-full border-0 border-b border-teal/30 bg-transparent px-0 py-3 text-base text-teal outline-none transition-colors placeholder:text-teal/50 focus:border-teal";

export default function Contact() {
  const [params] = useSearchParams();
  const about = params.get("about");
  const [form, setForm] = useState({
    name: "",
    email: "",
    organisation: "",
    subject: SUBJECTS.includes(params.get("subject")) ? params.get("subject") : "General",
    message: about ? `I'd like to know more about ${about}.` : "",
  });
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await api.post("/enquiries", form);
      setState("sent");
      toast.success("Thanks, your enquiry has been sent.");
    } catch (err) {
      setState("idle");
      toast.error(formatError(err));
    }
  };

  return (
    <main data-testid="contact-page">
      <PageHeader testId="contact-header" eyebrow="Contact" lines={["Let's grow", "something resilient."]} intro="Tell us about your crop, your project or your partnership idea. We'll get back to you by email." />
      <section className="bg-paper py-20 sm:py-28">
        <div className="mx-auto grid max-w-[1440px] gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
          <Reveal className="lg:col-span-4">
            <p className="label-caps text-moss">Write to us</p>
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-link" className="group mt-6 inline-flex items-center gap-2 break-all font-display text-2xl text-teal sm:text-3xl">
              <span className="link-underline">{CONTACT_EMAIL}</span>
              <ArrowUpRight className="h-5 w-5 shrink-0 transition-transform group-hover:rotate-45" />
            </a>
            <div className="mt-12 border-t border-teal/15 pt-8">
              <p className="font-display text-xl text-teal">Bruno Gatimel</p>
              <p className="mt-1 text-sm text-teal-ink/80">Managing Director, Bio Smart Solutions Limited</p>
              <p className="mt-6 text-sm leading-relaxed text-teal-ink/80">New Zealand · Australia · Pacific Islands</p>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-7 lg:col-start-6" delay={0.1}>
            {state === "sent" ? (
              <div data-testid="contact-success" className="flex min-h-[420px] flex-col items-start justify-center bg-mist p-10">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-teal text-mist"><Check className="h-6 w-6" /></span>
                <h2 className="mt-8 font-display text-4xl text-teal">Thank you.</h2>
                <p className="mt-4 max-w-md text-teal-ink/85">Your enquiry has been received. We'll reply to {form.email} as soon as possible.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="grid gap-8 sm:grid-cols-2">
                <Field label="Name *">
                  <input required data-testid="contact-name-input" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
                </Field>
                <Field label="Email *">
                  <input required type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@farm.co.nz" />
                </Field>
                <Field label="Organisation">
                  <input data-testid="contact-organisation-input" value={form.organisation} onChange={set("organisation")} className={inputCls} placeholder="Farm, company or group" />
                </Field>
                <Field label="Subject">
                  <select data-testid="contact-subject-select" value={form.subject} onChange={set("subject")} className={`${inputCls} cursor-pointer`}>
                    {SUBJECTS.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Message *">
                    <textarea required rows={6} data-testid="contact-message-input" value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Tell us about your crop, pest challenge or project" />
                  </Field>
                </div>
                <button
                  type="submit"
                  disabled={state === "sending"}
                  data-testid="contact-submit-btn"
                  className="group label-caps inline-flex items-center justify-center gap-3 self-start rounded-full bg-teal px-9 py-4 !text-[11px] text-mist transition-colors duration-300 hover:bg-teal-ink disabled:opacity-60 sm:col-span-2 sm:justify-self-start"
                >
                  {state === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Send enquiry
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
